import { animate, createTimeline, stagger } from '../../vendor/anime.esm.js';
import { DURATION, EASE, STAGGER, SUPPORTS_CLIP, resolveDuration, resolveStagger } from '../motion-tokens.js';
import { esDecoracion } from '../fondos/decoracion.js';
import { rehacerFondoDeSeccion } from '../backgrounds.js';

/** Alto REAL del header fijo, que `render.js::applyFirstSectionHeaderOffset` publica en
 *  `--header-h` (raíz). El panel sticky se ancla DEBAJO de él: con `top: 0` el header le
 *  tapaba el título y el subtítulo, y `applyFirstSectionHeaderOffset` no podía ayudar porque
 *  un hijo `position: sticky` se ancla al VIEWPORT, no a la sección. Espejo del
 *  `.tl-ss-sticky` de site.css: si cambias uno, cambia el otro. */
const alturaHeader = () => {
    const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h'));
    return Number.isFinite(v) && v > 0 ? v : 0;
};

/** Marca un nodo como "lo escondió una coreografía". `anime-orchestrador.js::revelarSinAnimacion`
 *  lo busca por este atributo para devolverlo a la vista cuando la coreografía falla a medias:
 *  esconder los hijos originales y no volver a mostrarlos deja la sección MEDIO construida
 *  (una franja vacía donde estaba la timeline), y ese estado no lo arregla ninguna otra red. */
const ATTR_OCULTO = 'data-anim-oculto';

/** Cuánto scroll cuesta revelar un hito, como fracción del alto del panel. Era 0.75 (tres
 *  cuartos de pantalla POR HITO): con 5 hitos había que recorrer casi cuatro pantallas para
 *  llegar al final, y el visitante que no lo intuía se quedaba mirando una tarjeta suelta.
 *  Con 0.45 la historia entera cabe en poco más de dos. */
const FRACCION_POR_HITO = 0.45;

export const timelineSticky = (el) => {
    if (window.innerWidth <= 768) return null;

    const section = el.querySelector('section');
    if (!section) return null;

    const rawItems = el.querySelectorAll('.tl-item');
    if (!rawItems.length) return null;

    const items = Array.from(rawItems).map((item) => ({
        year:  item.querySelector('[data-field="year"]')?.textContent?.trim() || '',
        title: item.querySelector('[data-field="tl-title"]')?.textContent?.trim() || '',
        desc:  item.querySelector('[data-field="tl-desc"]')?.textContent?.trim() || '',
        side:  item.classList.contains('tl-right') ? 'right' : 'left',
    }));

    const N = items.length;
    // Alto del panel pegado = ventana MENOS el header fijo (lo mismo que calcula el CSS). El
    // recorrido se mide contra él y no contra `innerHeight`, o el último hito se revelaría
    // fuera del tramo en el que el panel sigue pegado.
    let stickyTop = alturaHeader();
    let stickyH   = Math.max(320, window.innerHeight - stickyTop);
    let PX_PER_STEP = stickyH * FRACCION_POR_HITO;

    const titleEl    = section.querySelector('[data-field="title"]');
    const subtitleEl = section.querySelector('[data-field="subtitle"]');
    const titleText    = titleEl?.textContent?.trim() || '';
    const subtitleText = subtitleEl?.textContent?.trim() || '';

    const hiddenChildren = [];
    const mostrarOriginales = () => hiddenChildren.forEach(child => {
        if (!child) return;
        child.style.display = '';
        child.removeAttribute(ATTR_OCULTO);
    });
    Array.from(section.children).forEach(child => {
        if (child.classList.contains('tl-ss-outer')) return;
        // La DECORACIÓN de la sección no se esconde: no es el contenido que sustituye el panel
        // (`animations/fondos/decoracion.js`, el mismo predicado que usa el orquestador). Entre
        // ella, el hueco del separador por RECORTE —esconderlo subiría la sección los 60-200 px
        // de la banda y el panel pegado se comería la cuña del vecino de arriba— y el FONDO
        // ANIMADO, que antes se escondía con el resto: se veía en el editor y nunca en «Ver sitio»
        // ni en el publicado (L5 del plan del motor de fondos).
        if (esDecoracion(child)) return;
        child.style.display = 'none';
        child.setAttribute(ATTR_OCULTO, '1');
        hiddenChildren.push(child);
    });

    // A partir de aquí los hijos originales YA están ocultos: cualquier fallo tiene que
    // devolverlos, o la sección se queda en blanco (el bug "solo me pasó esa vez" —
    // irreproducible justamente porque depende de que algo intermedio lance).
    let rafId = null;   // fuera del try: el catch tiene que poder cancelar el rAF
    try {

    const outer = document.createElement('div');
    outer.className = 'tl-ss-outer';
    outer.style.height = `${PX_PER_STEP * N + stickyH}px`;

    const sticky = document.createElement('div');
    sticky.className = 'tl-ss-sticky';
    const cs = getComputedStyle(section);
    // La fuente es la que la sección PINTA (`#resultado section` → --font-body ya resuelta). El
    // `--font` que inyecta el render es legado y lleva el ID del preset ('industrial', 'warm'…), no
    // una familia: copiarlo pintaba los hitos con la serif del sistema (medido 01-10-2026).
    sticky.style.cssText = `
        --bg: ${cs.getPropertyValue('--bg').trim() || '#09131e'};
        --text: ${cs.getPropertyValue('--text').trim() || '#e8f0f7'};
        --accent: ${cs.getPropertyValue('--accent').trim() || '#6366f1'};
        font-family: ${cs.fontFamily};
    `;

    const header = document.createElement('div');
    header.className = 'tl-ss-header';
    header.innerHTML = `<h2>${titleText}</h2>${subtitleText ? `<p>${subtitleText}</p>` : ''}`;
    sticky.appendChild(header);

    const canvas = document.createElement('div');
    canvas.className = 'tl-ss-canvas';

    const spine = document.createElement('div');
    spine.className = 'tl-ss-spine';
    canvas.appendChild(spine);

    const progress = document.createElement('div');
    progress.className = 'tl-ss-progress';

    // El indicador ya no es solo "scroll y desaparece": ahora dice CUÁNTO queda ("2 / 5") y
    // sigue visible hasta el último hito. Se ocultaba a los 80 px de scroll, así que en cuanto
    // el visitante empezaba a bajar perdía la única pista de que la sección aún tenía historia
    // que contar. El texto son NÚMEROS (no necesita traducción); la palabra 'scroll' ya estaba.
    const hint = document.createElement('div');
    hint.className = 'tl-ss-hint';
    const hintCount = document.createElement('span');
    hintCount.textContent = `1 / ${N}`;   // visible desde el primer pintado (antes: vacío hasta entrar en el tramo)
    hintCount.className = 'tl-ss-hint-count';
    hint.innerHTML = `<span>scroll</span><div class="tl-ss-hint-arrow"></div>`;
    hint.insertBefore(hintCount, hint.firstChild);

    const HEADER_RESERVE = 140;
    const V_PAD          = 20;

    function getCanvasH() {
        return Math.max(160, stickyH - HEADER_RESERVE);
    }

    function getCardHalfH() {
        const ch = getCanvasH();
        const maxByN = Math.floor((ch - V_PAD * 2) / (N * 2));
        return Math.max(32, Math.min(70, maxByN));
    }

    function getDotY(i) {
        const ch   = getCanvasH();
        const chH  = getCardHalfH();
        const slotH = ch / N;
        const rawY  = slotH * i + slotH * 0.5;
        return Math.max(V_PAD + chH, Math.min(ch - V_PAD - chH, rawY));
    }

    function getCardTop(i) {
        const dotY = getDotY(i);
        const ch   = getCanvasH();
        const chH  = getCardHalfH();
        return Math.max(V_PAD, Math.min(ch - chH * 2 - V_PAD, dotY - chH));
    }

    const dotEls  = [];
    const cardEls = [];
    const pdEls   = [];

    items.forEach((item, i) => {
        const dot = document.createElement('div');
        dot.className = 'tl-ss-dot';
        dot.style.top = `${getDotY(i) - 7}px`;
        canvas.appendChild(dot);
        dotEls.push(dot);

        const card = document.createElement('div');
        card.className = 'tl-ss-card';
        card.innerHTML = `
            <span class="tl-ss-year">${item.year}</span>
            <div class="tl-ss-title">${item.title}</div>
            <div class="tl-ss-desc">${item.desc}</div>
            <div class="tl-ss-ghost">${item.year}</div>
        `;
        card.style.top = `${getCardTop(i)}px`;
        if (item.side === 'left') {
            card.style.right = '56%';
            card.style.left  = '4%';
        } else {
            card.style.left  = '56%';
            card.style.right = '4%';
        }
        card.style.width = '';
        canvas.appendChild(card);
        cardEls.push(card);

        const pd = document.createElement('div');
        pd.className = 'tl-ss-pd';
        progress.appendChild(pd);
        pdEls.push(pd);
    });

    sticky.appendChild(canvas);
    sticky.appendChild(progress);
    sticky.appendChild(hint);
    outer.appendChild(sticky);
    section.appendChild(outer);
    // La sección acaba de cambiar de alto (varias pantallas): su fondo animado, si lo lleva, tiene
    // que seguirla. Desde el P3 lo re-mide solo el ResizeObserver del motor de fondos y esta llamada
    // no rehace nada (misma firma); se conserva porque es gratis y deja escrito el requisito. El
    // panel es transparente cuando hay fondo (site.css), así que se ve detrás del recorrido.
    rehacerFondoDeSeccion(section);

    let spineTarget  = 0;
    let spineCurrent = 0;

    function animateSpine() {
        spineCurrent += (spineTarget - spineCurrent) * 0.14;
        if (Math.abs(spineTarget - spineCurrent) < 0.3) spineCurrent = spineTarget;
        spine.style.height = `${spineCurrent}px`;
        rafId = requestAnimationFrame(animateSpine);
    }
    rafId = requestAnimationFrame(animateSpine);

    let revealed = -1;

    function handleTimelineScroll() {
        if (!outer || !outer.parentNode) return;
        const rect = outer.getBoundingClientRect();
        // El panel empieza a pegarse cuando su borde superior llega a `top: --header-h`, no a 0:
        // midiendo con `-rect.top` el recorrido arrancaba con el header de desfase y el último
        // hito se revelaba cuando el panel ya se había despegado.
        const scrolled = stickyTop - rect.top;
        if (scrolled < 0) return;

        const progressRaw = scrolled / PX_PER_STEP;
        const step        = Math.min(Math.floor(progressRaw), N - 1);

        for (let i = 0; i <= step; i++) {
            if (i > revealed) {
                const idx = i;
                setTimeout(() => dotEls[idx]?.classList.add('on'),  80);
                setTimeout(() => cardEls[idx]?.classList.add('on'), 340);
                revealed = idx;
            }
        }

        spineTarget = step >= 0 ? getDotY(step) : 0;
        pdEls.forEach((pd, i) => pd.classList.toggle('on', i <= step));

        // El contador acompaña hasta el final; el indicador solo se apaga cuando ya no queda
        // nada por revelar (antes desaparecía a los 80 px y dejaba al visitante sin pista).
        hintCount.textContent = `${Math.min(step + 1, N)} / ${N}`;
        hint.classList.toggle('hidden', step >= N - 1);
    }

    window.addEventListener('scroll', handleTimelineScroll, { passive: true });
    handleTimelineScroll();

    function onResize() {
        if (!outer || !outer.parentNode) return;
        // El header cambia de alto al pasar a móvil o al cargar tarde el logo: se re-mide todo
        // (panel, recorrido y posiciones) o el recorrido dejaría de cuadrar con lo que se ve.
        stickyTop   = alturaHeader();
        stickyH     = Math.max(320, window.innerHeight - stickyTop);
        PX_PER_STEP = stickyH * FRACCION_POR_HITO;
        outer.style.height = `${PX_PER_STEP * N + stickyH}px`;
        dotEls.forEach((dot, i) => {
            dot.style.top = `${getDotY(i) - 7}px`;
            cardEls[i].style.top = `${getCardTop(i)}px`;
        });
        handleTimelineScroll();
    }
    window.addEventListener('resize', onResize);

    return {
        revert: () => {
            window.removeEventListener('scroll', handleTimelineScroll);
            window.removeEventListener('resize', onResize);
            if (rafId) cancelAnimationFrame(rafId);
            if (outer && outer.parentNode) outer.remove();
            mostrarOriginales();
            // Vuelve a su alto de siempre: el fondo, a su medida también.
            if (section.isConnected) rehacerFondoDeSeccion(section);
        },
        isCustomTimelineCleanup: true
    };

    } catch (e) {
        // Devolver la sección a su estado ESTÁTICO (que es la v1 entera, perfectamente
        // presentable) y quitar el andamio a medio montar. Sin esto, el orquestador atrapaba la
        // excepción pero solo limpiaba `opacity`: los hijos seguían en `display:none` y quedaba
        // una franja vacía con la cabecera tapada. Se relanza para que el orquestador lo avise.
        try {
            const zombi = section.querySelector('.tl-ss-outer');
            if (zombi) zombi.remove();
        } catch (_) { /* nada que hacer */ }
        mostrarOriginales();
        // Si el fallo llegó después de alargar la sección, su fondo se rehízo a esa medida.
        try { rehacerFondoDeSeccion(section); } catch (_) { /* el fondo es decoración */ }
        throw e;
    }
};

export const timelineV2 = (el) => {
    // Solo DESCENDIENTES: `[class*="line"]` casaba antes el propio <section class="… timeline-layout">
    // y le ponía clip-path + opacity a la sección entera (recortaba blob/ghost y quedaba pegado).
    const line   = el.querySelector('.tl-line, .timeline-line')
        || Array.from(el.querySelectorAll('[class*="line"]')).find(n => n !== el && n.tagName !== 'SECTION') || null;
    const cards  = el.querySelectorAll('.tl-card');
    const dots   = el.querySelectorAll('.tl-dot');

    if (line) { line.style.opacity = '0.3'; if (SUPPORTS_CLIP) line.style.clipPath = 'inset(0 0 100% 0)'; }
    dots.forEach(d => { d.style.opacity = '0'; d.style.transform = 'scale(0)'; });
    cards.forEach(c => { c.style.opacity = '0'; c.style.transform = 'translateX(40px)'; c.style.filter = 'blur(3px)'; });

    const tl = createTimeline({ defaults: { ease: EASE.refined }, });

    if (line) tl.add(line, {
        clipPath: ['inset(0 0 100% 0)', 'inset(0 0 0% 0)'],
        opacity:  [0.3, 1],
        duration: resolveDuration(DURATION.slow * 1.5),
        ease:     'linear',
    });

    if (dots.length) tl.add(dots, {
        scale:   [0, 1],
        opacity: [0, 1],
        delay:   stagger(resolveStagger(STAGGER.normal)),
        duration: resolveDuration(DURATION.fast),
        ease:    EASE.bouncy,
    }, '-=600');

    if (cards.length) tl.add(cards, {
        opacity:    [0, 1],
        translateX: [40, 0],
        filter:     ['blur(3px)', 'blur(0px)'],
        delay:      stagger(resolveStagger(STAGGER.normal)),
        duration:   resolveDuration(DURATION.slow),
    }, '-=400');

    return tl;
};

export const timelineV3 = (el) => {
    const cards = el.querySelectorAll('.tl-card, .timeline-event, .tl-item');
    if (!cards.length) return null;

    return animate(cards, {
        opacity:    [0, 1],
        translateX: [80, 0],
        filter:     ['blur(4px)', 'blur(0px)'],
        delay:      stagger(resolveStagger(STAGGER.fast), { start: 0 }),
        duration:   resolveDuration(DURATION.slow),
        ease:       EASE.snappy,
    });
};
